import {
  createColumnHelper,
  flexRender,
  type SortingState,
  useTable
} from '@tanstack/react-table'
import { Link } from 'react-router'
import { PayloadSchema, PayloadSchemas } from '../../types/types'
import { toLocaleDateString } from '../../utils/dateUtils'
import { ArrowRightIcon, Mtds } from '../../index'
import { useMemo, useState } from 'react'
import { sortableTableFeatures } from '../tableFeatures'

type SchemasTableProps = {
  schemas: PayloadSchemas
  contractId?: number
}

const getSchemaPath = (schemaId: number, contractId?: number) =>
  contractId
    ? `/contracts/${contractId}/schemas/${schemaId}`
    : `/schemas/${schemaId}`

const SchemaActionsCell = ({
  schemaId,
  contractId
}: {
  schemaId: number
  contractId?: number
}) => (
  <Mtds.Flex data-justify='end'>
    <Link
      id={`schema-link-${schemaId}`}
      to={getSchemaPath(schemaId, contractId)}
      aria-label='Gå til skjema'
      className='inline-flex items-center no-underline'
    >
      <ArrowRightIcon data-size='lg' aria-hidden />
    </Link>
  </Mtds.Flex>
)

export const SchemasTable = ({ schemas, contractId }: SchemasTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<
      typeof sortableTableFeatures,
      PayloadSchema
    >()
    return columnHelper.columns([
      columnHelper.accessor('name', {
        header: 'Navn',
        cell: (info) => info.getValue()
      }),
      columnHelper.accessor('lastUpdated', {
        header: 'Sist oppdatert',
        cell: (info) => toLocaleDateString(info.getValue())
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <SchemaActionsCell
            schemaId={info.row.original.id}
            contractId={contractId}
          />
        )
      })
    ])
  }, [contractId])
  const sortedSchemas = useMemo(
    () => schemas.slice().sort((a, b) => a.id - b.id),
    [schemas]
  )
  const table = useTable({
    features: sortableTableFeatures,
    data: sortedSchemas,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting
  })
  return (
    <figure>
      <Mtds.Table data-align='center' aria-label='Skjemaer' data-nowrap>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Mtds.Table.ThSortable
                  id={header.id}
                  key={header.id}
                  aria-sort={header.column.getIsSorted()}
                  onClick={
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Mtds.Table.ThSortable>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              id={row.id}
              className={'hover:bg-surface-hover cursor-pointer'}
              data-clickdelegatefor={`schema-link-${row.original.id}`}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Mtds.Table>
    </figure>
  )
}
